import React, { useEffect, useState } from 'react';
import { Table, Space, Button, Modal, Input, Tag } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useDispatch, useSelector } from 'react-redux';
import { getContactUs, updateContactStatus } from '../../actions/business';
import SearchFilter from '@components/Comman/TableFilter/SearchFilter';

const { TextArea } = Input;

interface DataType {
    key: string;
    companyName: string;
    contactName: string;
    email: string;
    resolved: boolean;
    abn: string;
    id : string;
    contact_type?: string | null;
}

interface TicketType {
    type?: string;
    contactType?: string;
}

const RaiseTicket = ({ type, contactType }: TicketType) => {
    const [data, setData] = useState<DataType[]>([]);
    const [pagination, setPagination] = useState({ totalItems: 0 });
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editRecord, setEditRecord] = useState<DataType | null>(null);
    const [editContent, setEditContent] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [reload, setReload] = useState(false);

    const dispatch = useDispatch();
    const { isLoading } = useSelector((state: any) => state?.General);

    useEffect(() => {
        const payload = { limit: pageSize, page, isTicket: type, contactType };
        dispatch(
            getContactUs(payload, (resp: any) => {
                if (resp?.status === 200) {
                    setData(resp?.data?.data);
                    setPagination(resp?.data?.pagination);
                }
            })
        );
    }, [page, pageSize, reload, dispatch]);

    const handleTableChange = (pagination: any) => {
        setPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const showEditModal = (record: DataType) => {
        setEditRecord(record);
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        if (editContent.length < 10) {
            setError("Content must be at least 10 characters long.");
            return;
        }
        setError(null);  // Clear any previous errors

        let payload = {
            id: editRecord?.id,
            remarks: editContent
        }

        dispatch(
            updateContactStatus(payload, (resp: any) => {
                if (resp?.status === 200) {
                    setReload(prev => !prev);
                }
            })
        );
        setIsModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditRecord(null);
    };

    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditContent(e.target.value);
    };

    const baseColumns: ColumnsType<DataType> = [
        {
            title: 'Business Name',
            dataIndex: 'companyName',
            key: 'companyName',
            sorter: (a, b) => a.companyName.localeCompare(b.companyName),
            align: 'left',
            width: 200,
        },
        {
            title: 'Contact Person',
            dataIndex: 'contactName',
            key: 'contactName',
            sorter: (a, b) => a.contactName.localeCompare(b.contactName),
            align: 'left',
            width: 180,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            align: 'left',
            width: 200,
        },
        {
            title: 'Mobile',
            dataIndex: type === 'ticket' ? 'contactNumber' : 'mobileNumber',
            key: type === 'ticket' ? 'contactNumber' : 'mobileNumber',
            align: 'center',
            width: 130,
        },
    ];

    // Add Contact Type column for non-ticket types
    if (type !== 'ticket') {
        baseColumns.push({
            title: 'Contact Type',
            dataIndex: 'contact_type',
            key: 'contact_type',
            align: 'left' as const,
            width: 220,
            render: (text: any) => text ? text : '--',
        });
    }

    // Add Description column
    baseColumns.push({
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        align: 'left',
        width: 400,
        render: (text) => text ? text : '--',
    });

    // Add Status column for tickets
    if (type === 'ticket') {
        baseColumns.push({
            title: 'Status',
            key: 'resolved',
            align: 'center' as const,
            width: 90,
            render: (_, record) => (
                <span>
                    {record.resolved ? <Tag color="green">Success</Tag> : <Tag color="red">Pending</Tag>}
                </span>
            ),
        });
    }

    // Add Action column
    baseColumns.push({
        title: 'Action',
        key: 'action',
        align: 'center',
        width: 100,
        render: (_, record) => (
            <Space size="middle">
                {!record.resolved ? (
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    />
                ) : null}
            </Space>
        ),
    });

    const columns = baseColumns;


    const advanceBusinessList = [
        { value: "companyName", label: "Business Name" },
        { value: "contactPerson", label: "Contact Person" },
        { value: "email", label: "Email" },
    ];

    const handleSearch = (value:any) =>{
        const payload = { limit: pageSize, page, isTicket: type, ...value };
        dispatch(
            getContactUs(payload, (resp: any) => {
                if (resp?.status === 200) {
                    setData(resp?.data?.data);
                    setPagination(resp?.data?.pagination);
                }
            })
        );
    }

    return (
        <div className="responsivetable">
            <div className=''>
                <SearchFilter
                    searchTitle="Business Name, Contact Name"
                    filterList={advanceBusinessList}
                    onSearch={(searchValue, ) => {
                        // Handle the search value here
                        handleSearch(searchValue)
                    }}
                />
            </div>
            <Table
                size="small"
                columns={columns}
                dataSource={data}
                loading={isLoading}
                pagination={{
                    total: pagination?.totalItems || 0,
                    pageSize,
                    current: page,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50'],
                }}
                scroll={{ y: 264, x: 800 }}
                onChange={handleTableChange}
                rowKey="id"
            />

            <Modal
                title="Resolve"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Submit"
            >
                <TextArea
                    rows={4}
                    value={editContent}
                    onChange={handleContentChange}
                    placeholder="Remarks here..."
                    minLength={10}
                    required={true}
                />
                {error && <p style={{ color: 'red' }}>{error}</p>}  {/* Show error message */}
            </Modal>
        </div>
    );
};

export default RaiseTicket;
